import React, { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Inline, Stack, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { main_servicedeskSettingsVirtualAgentLanding_LandingPage_featuresRef$key } from '@atlassian/jira-relay/src/__generated__/main_servicedeskSettingsVirtualAgentLanding_LandingPage_featuresRef.graphql';
import type { main_servicedeskSettingsVirtualAgentLanding_LandingPage_projectRef$key } from '@atlassian/jira-relay/src/__generated__/main_servicedeskSettingsVirtualAgentLanding_LandingPage_projectRef.graphql';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { ActionSection } from './action-section';
import { Details } from './details';
import { Header } from './header';

export const LandingPage: FC<{
	projectRef: main_servicedeskSettingsVirtualAgentLanding_LandingPage_projectRef$key;
	featuresRef: main_servicedeskSettingsVirtualAgentLanding_LandingPage_featuresRef$key;
}> = ({ projectRef, featuresRef }) => {
	const project = useFragment(
		graphql`
			fragment main_servicedeskSettingsVirtualAgentLanding_LandingPage_projectRef on JiraProject {
				...actionSection_servicedeskSettingsVirtualAgentLanding_projectRef
			}
		`,
		projectRef,
	);

	const features = useFragment(
		graphql`
			fragment main_servicedeskSettingsVirtualAgentLanding_LandingPage_featuresRef on VirtualAgentFeatures
			@argumentDefinitions(checkForAiEnabledFF: { type: "Boolean!" }) {
				isVirtualAgentAvailable
				...actionSection_servicedeskSettingsVirtualAgentLanding_featuresRef
					@arguments(checkForAiEnabledFF: $checkForAiEnabledFF)
				...header_servicedeskSettingsVirtualAgentLanding
			}
		`,
		featuresRef,
	);

	const { isVirtualAgentAvailable } = features;
	return (
		<>
			<Stack xcss={containerStyles} space="space.800">
				<Inline space="space.800" spread="space-between" alignInline="start">
					<Stack alignBlock="start" alignInline="start" space="space.300" xcss={textStyles}>
						<Header featuresRef={features} />
						<ActionSection projectRef={project} featuresRef={features} />
					</Stack>
				</Inline>
				<Details />
			</Stack>
			{/* https://data-portal.internal.atlassian.com/analytics/registry/57789 */}
			<FireScreenAnalytics attributes={{ isVirtualAgentAvailable }} />
			{/* SpaStatePageReady is used to indicate that the app can move to the "afterPaint" phase in react-loosely-lazy. */}
			{fg('jsm_simplified_project_settings_navigation') && <SpaStatePageReady />}
		</>
	);
};

const textStyles = xcss({
	minWidth: '300px',
	width: '100%',
});

const containerStyles = xcss({
	marginTop: 'space.400',
	marginLeft: 'space.negative.050',
	marginBottom: 'space.400',
});
